import { useEffect } from 'react';
import { trackEvent, setUserProperties } from '../utils/analytics';
import { useStandaloneMode } from './useStandaloneMode';

/**
 * Reports PWA-install signals to GA4. Mount ONCE near the app root.
 *
 * Two signals (GA4 has no built-in PWA-install metric):
 *   1. `display_mode` user property — 'standalone' when the app is launched as
 *      an installed PWA, else 'browser'. This is the ONLY way to gauge iOS
 *      installs: iOS Safari fires no install event, so installs can only be
 *      inferred from later standalone launches. Build a GA4 audience on
 *      `display_mode = standalone` for "installed users".
 *   2. `pwa_install` event — fired on the browser's `appinstalled` event, the
 *      real install moment on Android / desktop Chromium (never on iOS).
 */
export function usePwaInstallAnalytics() {
  const { isStandalone } = useStandaloneMode();

  // Tag the user with how they're running the app. Re-fires if the display
  // mode flips mid-session (e.g. the user installs and relaunches standalone).
  useEffect(() => {
    setUserProperties({ display_mode: isStandalone ? 'standalone' : 'browser' });
  }, [isStandalone]);

  // The real install moment (Chromium only — iOS never fires this).
  useEffect(() => {
    const onInstalled = () => trackEvent('pwa_install');
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);
}
